import React, { useState } from 'react';
import {
  Building,
  Plus,
  Wrench,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Sparkles,
  ShieldCheck,
  Eye,
  Layers,
  Clock,
} from 'lucide-react';
import { CommonAsset } from '../../types';
import { INITIAL_COMMON_ASSETS } from '../../data/adminModulesData';

export const AdminAssetsView: React.FC = () => {
  const [assets, setAssets] = useState<CommonAsset[]>(INITIAL_COMMON_ASSETS);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAssetForDetails, setSelectedAssetForDetails] = useState<CommonAsset | null>(null);

  // New Asset State
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newCategory, setNewCategory] = useState<CommonAsset['category']>('clubhouse');
  const [newLocation, setNewLocation] = useState('');
  const [newValue, setNewValue] = useState(100000);
  const [newResponsible, setNewResponsible] = useState('ผู้จัดการนิติบุคคล');

  const filteredAssets = assets.filter((a) => {
    return selectedCategory === 'all' || a.category === selectedCategory;
  });

  const totalAssetValue = assets.reduce((acc, curr) => acc + curr.estimated_value, 0);

  const handleAddAsset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newCode) return;

    const newAsset: CommonAsset = {
      id: `ast-${Date.now()}`,
      code: newCode,
      name: newName,
      category: newCategory,
      location: newLocation || 'พื้นที่ส่วนกลาง',
      status: 'good',
      estimated_value: Number(newValue),
      installed_date: new Date().toISOString().split('T')[0],
      warranty_until: '2028-12-31',
      last_inspection: new Date().toISOString().split('T')[0],
      next_maintenance: '2026-11-30',
      responsible_staff: newResponsible,
    };

    setAssets([newAsset, ...assets]);
    setShowAddModal(false);
    setNewName('');
    setNewCode('');
  };

  const updateAssetStatus = (id: string, newStatus: CommonAsset['status']) => {
    setAssets((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: newStatus, last_inspection: new Date().toISOString().split('T')[0] } : item
      )
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200">
            <Building className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              4. งานบริหารทรัพย์ส่วนกลาง (Common Property & Facility Management)
            </h2>
            <p className="text-xs text-slate-500">
              ทะเบียนทรัพย์สินนิติบุคคล, แผนซ่อมบำรุงเชิงป้องกัน (PM), บันทึกการตรวจสอบสภาพ
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
        >
          <Plus className="w-4 h-4" /> เพิ่มทรัพย์สินส่วนกลาง
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">ทรัพย์สินขึ้นทะเบียน</span>
          <p className="text-2xl font-bold text-slate-900 font-mono">{assets.length} รายการ</p>
          <p className="text-[11px] text-slate-400">ครบถ้วนทุกหมวดหมู่งานระบบ</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">มูลค่าทรัพย์สินส่วนกลางรวม</span>
          <p className="text-2xl font-bold text-emerald-700 font-mono">
            ฿{totalAssetValue.toLocaleString()}
          </p>
          <p className="text-[11px] text-emerald-600">สินทรัพย์ภายใต้การดูแลของนิติ</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">สถานะพร้อมใช้งาน (100%)</span>
          <p className="text-2xl font-bold text-blue-700 font-mono">
            {assets.filter((a) => a.status === 'good' || a.status === 'in_use').length} / {assets.length}
          </p>
          <p className="text-[11px] text-blue-600 font-medium">ตรวจสภาพสม่ำเสมอทุกสัปดาห์</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">การตรวจบำรุงรอบถัดไป</span>
          <p className="text-2xl font-bold text-indigo-700 font-mono">21 ส.ค. 69</p>
          <p className="text-[11px] text-indigo-600">ล้างระบบกรองทรายสระว่ายน้ำ</p>
        </div>
      </div>

      {/* Asset Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAssets.map((asset) => (
          <div
            key={asset.id}
            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs flex flex-col justify-between space-y-4 hover:border-slate-300 transition-all"
          >
            <div className="space-y-2">
              <div className="flex items-start justify-between">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono bg-slate-100 text-slate-700 border border-slate-200">
                  {asset.code}
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                    asset.status === 'good'
                      ? 'bg-emerald-100 text-emerald-800'
                      : asset.status === 'in_use'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {asset.status === 'good' && '🟢 สภาพสมบูรณ์'}
                  {asset.status === 'in_use' && '🔵 เปิดใช้งานปกติ'}
                  {asset.status === 'needs_repair' && '🟡 รอซ่อมแซม'}
                  {asset.status === 'maintenance' && '🔧 กำลังบำรุงรักษา'}
                </span>
              </div>

              <h3 className="font-bold text-slate-900 text-sm leading-snug">{asset.name}</h3>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                📍 {asset.location}
              </p>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl space-y-1.5 text-xs text-slate-600 font-mono">
              <div className="flex justify-between">
                <span>มูลค่าประเมิน:</span>
                <span className="font-bold text-slate-900">฿{asset.estimated_value.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>ตรวจสภาพล่าสุด:</span>
                <span>{new Date(asset.last_inspection).toLocaleDateString('th-TH')}</span>
              </div>
              <div className="flex justify-between text-blue-700">
                <span>รอบบำรุงรักษาถัดไป:</span>
                <span className="font-bold">{new Date(asset.next_maintenance).toLocaleDateString('th-TH')}</span>
              </div>
              <div className="flex justify-between text-[11px] text-slate-400">
                <span>ผู้รับผิดชอบ:</span>
                <span className="font-sans">{asset.responsible_staff}</span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
              <select
                value={asset.status}
                onChange={(e) => updateAssetStatus(asset.id, e.target.value as any)}
                className="text-xs p-1.5 rounded-lg border border-slate-200 bg-white font-medium text-slate-700 cursor-pointer"
              >
                <option value="good">สภาพสมบูรณ์</option>
                <option value="in_use">เปิดใช้งานปกติ</option>
                <option value="maintenance">กำลังบำรุงรักษา</option>
                <option value="needs_repair">แจ้งซ่อม</option>
              </select>

              <button
                onClick={() => setSelectedAssetForDetails(asset)}
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1 cursor-pointer"
              >
                <Eye className="w-3.5 h-3.5" /> รายละเอียด
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Detail Modal */}
      {selectedAssetForDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base">{selectedAssetForDetails.name}</h3>
                <p className="text-xs text-slate-400 font-mono">{selectedAssetForDetails.code}</p>
              </div>
              <button onClick={() => setSelectedAssetForDetails(null)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-xl">
                  <span className="text-slate-400 block mb-1">ตำแหน่งที่ตั้ง</span>
                  <span className="font-semibold text-slate-800">{selectedAssetForDetails.location}</span>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl">
                  <span className="text-slate-400 block mb-1">มูลค่าทรัพย์สิน</span>
                  <span className="font-bold text-emerald-700 text-sm">฿{selectedAssetForDetails.estimated_value.toLocaleString()}</span>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-bold text-slate-800">ข้อมูลการรับประกันและบำรุงรักษา</h4>
                <div className="p-3 bg-slate-50 rounded-xl space-y-1.5 font-mono">
                  <div className="flex justify-between">
                    <span>วันที่ติดตั้ง/เริ่มใช้งาน:</span>
                    <span>{selectedAssetForDetails.installed_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>รับประกันถึง:</span>
                    <span>{selectedAssetForDetails.warranty_until}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ผู้ดูแลรับผิดชอบ:</span>
                    <span className="font-sans">{selectedAssetForDetails.responsible_staff}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-3 border-t border-slate-100">
                <button
                  onClick={() => setSelectedAssetForDetails(null)}
                  className="px-5 py-2 bg-blue-700 text-white rounded-xl font-bold"
                >
                  ปิดหน้าต่าง
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <h3 className="font-bold text-base">เพิ่มทะเบียนทรัพย์สินส่วนกลาง</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <form onSubmit={handleAddAsset} className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">รหัสทรัพย์สิน *</label>
                  <input
                    type="text"
                    required
                    placeholder="เช่น AST-PUMP-02"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">หมวดหมู่ *</label>
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value as any)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="clubhouse">อาคารสโมสรคลับเฮาส์</option>
                    <option value="pool">สระว่ายน้ำ & ระบบบำบัด</option>
                    <option value="fitness">ฟิตเนส & อุปกรณ์ออกกำลังกาย</option>
                    <option value="security_cctv">ระบบ CCTV & รักษาความปลอดภัย</option>
                    <option value="water_pump">ระบบประปา & ปั๊มน้ำ</option>
                    <option value="lighting">ระบบไฟฟ้าส่องสว่าง</option>
                    <option value="park_garden">สวนส่วนกลาง & สนามเด็กเล่น</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">ชื่อรายการทรัพย์สิน *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น มอเตอร์ปั๊มน้ำสำรอง 5HP"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ตำแหน่งที่ตั้ง</label>
                  <input
                    type="text"
                    placeholder="เช่น ห้องเครื่องสระว่ายน้ำ"
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">มูลค่าประเมิน (บาท)</label>
                  <input
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">เจ้าหน้าที่ผู้รับผิดชอบ</label>
                <input
                  type="text"
                  value={newResponsible}
                  onChange={(e) => setNewResponsible(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold"
                >
                  บันทึกทะเบียน
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
